import { Component, Input, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, combineLatest, pipe } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { untilDestroyed } from '@app/core/helpers/until-destroyed';
import { AppStateService, EnvironmentService, DialogNames, ModalsService } from '@app/core/services';
import { ChannelService } from '@app/data/services/channel.service';
import { UserInfo, ChannelCategories } from '@app/data/models';
import { LoadingState } from '@app/core/state/channels-list';
import { ConfirmationDialogComponent } from '@shared/dialogs';
import { ChannelFormComponent } from '@app/modules/manage-channels/channel-form/channel-form.component';
import { CollectionFormComponent } from '@app/modules/manage-channels/collection-form/collection-form.component';
import { CategoryFormComponent } from '@app/modules/manage-channels/category-form/category-form.component';
import { ExportChannelComponent } from '@app/modules/manage-channels/export-channel/export-channel.component';

@Component({
    selector: 'app-channels-list',
    templateUrl: './channels-list.component.html',
    styleUrls: ['./channels-list.component.scss'],
    standalone: false,
})
export class ChannelsListComponent implements OnInit {
    @Input() userData: UserInfo = null;
    @Input() hasAdminRights = false;
    @Input() canAccessSuperadminSection = false;

    channelsCategories: any[];
    showEditCategory: boolean;
    showReadAndUnreadPosts: boolean;
    showEditCollection: boolean;
    showEditChannel: boolean;
    isExpandedFolders: Record<string, boolean> = {};
    routeParams: Params;

    isChannelsLoading$: Observable<boolean>;
    channelsListIsEmpty$: Observable<boolean>;

    private router = inject(Router);
    private appStateService = inject(AppStateService);
    private environmentService = inject(EnvironmentService);
    private modalsService = inject(ModalsService);
    private channelService = inject(ChannelService);
    private route = inject(ActivatedRoute);
    private untilDestroy = untilDestroyed();

    ngOnInit(): void {
        this.setEnvironmentVariables();
        this.setChannelsState();
        this.setChannelsList();
        this.unfoldCollectionWithActivatedChannel();
        this.watchQueryParams();
        this.appStateService.getChannelsList();
    }

    toggleEditCategoryModal(category) {
        this.router.navigate([], { queryParams: { modal: 'edit_category', categoryId: category.id } });
    }

    toggleEditCollectionModal(collectionId) {
        this.router.navigate([], { queryParams: { modal: 'edit_collection', collectionId: collectionId } });
    }

    toggleChannelFormModal(id = null) {
        if (id) {
            this.router.navigate([], { queryParams: { modal: 'edit_chanel', channelId: id } });
        }
    }

    toggleExportChannelModal(id) {
        this.router.navigate([], { queryParams: { modal: 'export_chanel', channelId: id } });
    }

    onCollapseFolder(folderId: string, isExpanded: boolean) {
        this.isExpandedFolders[folderId] = isExpanded;
    }

    toggleDeleteChannel(id) {
        this.modalsService
            .openDialog(DialogNames.Confirmation, ConfirmationDialogComponent, {
                header: 'Delete channel',
                message: 'Are you sure you would like to delete this channel?',
                confirm: 'Delete',
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                tap(() => {
                    this.channelService.deleteChannel(id).subscribe(() => {
                        this.redirectAfterChannelDelition(id);
                        this.appStateService.getChannelsList();
                    });
                }),
            )
            .subscribe();
    }

    unsubscribeChannel(channelId) {
        this.modalsService
            .openDialog(DialogNames.Confirmation, ConfirmationDialogComponent, {
                message: 'Are you sure you would like to unsubscribe from this channel?',
                confirm: 'Yes',
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result && !!this.userData?.user?.id),
                switchMap(() => this.channelService.deleteChannelSubscription(this.userData.user.id, channelId)),
                tap(() => this.appStateService.getChannelsList()),
            )
            .subscribe();
    }

    unsubscribeCollection(collectionId) {
        this.modalsService
            .openDialog(DialogNames.Confirmation, ConfirmationDialogComponent, {
                message: 'Are you sure you would like to unsubscribe from this collection?',
                confirm: 'Yes',
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result && !!this.userData?.user?.id),
                switchMap(() => this.channelService.deleteCollectionSubscription(this.userData.user.id, collectionId)),
                tap(() => this.appStateService.getChannelsList()),
            )
            .subscribe();
    }

    private openChannelFormModal = (channelId?) => {
        this.modalsService
            .openDialog(DialogNames.Channel, ChannelFormComponent, {
                channelId,
                isNotSubscribed: false,
            })
            .afterClosed()
            .pipe(
                tap((data) => {
                    if (data) {
                        if (data.isDeleted) {
                            this.redirectAfterChannelDelition(data.channelId);
                        } else {
                            this.router.navigate([], { queryParams: { updateFeed: true } });
                        }
                        this.appStateService.getChannelsList();
                    } else {
                        this.router.navigate([]);
                    }
                }),
            )
            .subscribe();
    };

    private openExportChannelModal = (channelId?) => {
        this.modalsService
            .openDialog(DialogNames.ExportChannel, ExportChannelComponent, {
                openExportChannelModal: this.openExportChannelModal,
                channelId: channelId,
            })
            .afterClosed()
            .pipe(
                tap(() => this.router.navigate([])),
                catchError((err) => {
                    this.router.navigate([]);
                    return err;
                }),
            )
            .subscribe();
    };

    // edit category
    private openEditCategoryModal = (categoryId?) => {
        this.modalsService
            .openDialog(DialogNames.Category, CategoryFormComponent, {
                categoryId,
            })
            .afterClosed()
            .pipe(this.onModalClosed())
            .subscribe();
    };

    private openCreateCollectionModal = () => {
        this.modalsService
            .openDialog(DialogNames.Collection, CollectionFormComponent, {})
            .afterClosed()
            .pipe(this.onModalClosed())
            .subscribe();
    };

    // edit collection modal
    private openEditCollectionModal = (collectionId) => {
        this.modalsService
            .openDialog(DialogNames.Collection, CollectionFormComponent, {
                collectionId,
            })
            .afterClosed()
            .pipe(this.onModalClosed())
            .subscribe();
    };

    get unreadStatusLoading(): Observable<boolean> {
        return this.appStateService.channelsListLoadingState$.pipe(
            map((channelsLoadingState) => channelsLoadingState !== LoadingState.LOADED_UNREAD_INFO),
        );
    }

    private watchQueryParams() {
        combineLatest([this.route.queryParams, this.appStateService.userInfo$])
            .pipe(
                this.untilDestroy(),
                filter(([params, userInfo]) => !!params && !!userInfo?.user),
                tap(([params]) => {
                    if (!this.routeParams || this.routeParams !== params) {
                        this.routeParams = params;
                        this.openMatchedModal(params);
                        if (Object.keys(params).length === 0) {
                            this.modalsService.closeAll();
                        }
                    }
                }),
            )
            .subscribe();
    }

    private openMatchedModal(params: Params) {
        const modalMatches = {
            edit_chanel: this.openChannelFormModal,
            create_collection: this.openCreateCollectionModal,
            edit_category: this.openEditCategoryModal,
            edit_collection: this.openEditCollectionModal,
            export_chanel: this.openExportChannelModal,
            delete_category: this.updateChannelsStructure,
        };

        const modalParam: string = params.modal;
        const entityId = params.channelId || params.collectionId || params.categoryId || null;
        if (modalMatches[modalParam]) {
            modalMatches[modalParam](entityId || null);
        }
    }

    private updateChannelsStructure = () => {
        this.appStateService.getChannelsList();
        this.router.navigate([]);
    };

    private redirectAfterChannelDelition(deletedChannelId: string) {
        this.appStateService
            .nextChannelId$(deletedChannelId)
            .pipe(
                take(1),
                tap((nextChannelId) => {
                    if (!nextChannelId) {
                        this.router.navigate(['/app/search']);
                        return;
                    } else {
                        this.router.navigate(['/app/channel', nextChannelId]);
                    }
                }),
            )
            .subscribe();
    }

    private setEnvironmentVariables() {
        this.showEditCategory = this.environmentService.showEditCategory;
        this.showReadAndUnreadPosts = this.environmentService.showReadAndUnreadPosts;
        this.showEditCollection = this.environmentService.showEditCollection;
        this.showEditChannel = this.environmentService.showEditChannel;
    }

    private setChannelsState() {
        this.isChannelsLoading$ = this.appStateService.channelsListLoadingState$.pipe(
            map((channelsLoadingState) => channelsLoadingState === LoadingState.LOADING),
        );
        this.channelsListIsEmpty$ = this.appStateService.channelsListIsEmpty$;
    }

    private setChannelsList() {
        this.appStateService.channelsList$
            .pipe(
                this.untilDestroy(),
                filter((categories) => !!categories),
                tap((categories: ChannelCategories[]) => {
                    this.channelsCategories = categories;
                }),
            )
            .subscribe();
    }

    private unfoldCollectionWithActivatedChannel() {
        this.appStateService.activatedChannelCollectionId$
            .pipe(
                filter((collectionId) => !!collectionId),
                tap((collectionId) => {
                    this.isExpandedFolders[collectionId] = true;
                }),
            )
            .subscribe();
    }

    private onModalClosed() {
        return pipe(
            tap(() => this.router.navigate([])),
            filter((needUpdate) => !!needUpdate),
            tap(() => this.appStateService.getChannelsList()),
        );
    }

    get adminOrSuperAdmin() {
        return this.hasAdminRights || this.canAccessSuperadminSection;
    }
}
